import { isPackageProduct } from "common/src/models/Product";
import { compact, flatMap, isEmpty, omit } from "lodash";
import pluralize from "pluralize";
import { InfiniteData, QueryKey } from "react-query";
import { ExperiencePurchaseItem, Waiver } from "types";
import { queryClient } from "@/api/queryClient";
import { getExpandedExperiencesInPackage, hasSelectedProductsExpanded } from "@/models/Package";

export const generateQueryKey = (prefix: QueryKey | string[], params: any, omitPaths: string[] = ["limit"]) => {
    let cacheKeyParam: any = omit(params, omitPaths);
    if (isEmpty(cacheKeyParam)) {
        cacheKeyParam = null;
    }

    if (Array.isArray(prefix)) {
        return compact([...prefix, cacheKeyParam]);
    }

    return compact([prefix, cacheKeyParam]);
};

/**
 * Pre-populate the cache for individual resources using expanded data.
 */
export const populateCache = (data: any, params: any) => {
    if (!Array.isArray(data) && data.expand) {
        for (const resource of data.expand) {
            if (resource.id && resource.object) {
                populateResource(pluralize(resource.object, 2), resource);
            }
        }
    } else if (Array.isArray(data.data) && params?.expand) {
        // Check if the `expand` query param is nested in the data instead of in an `expand`
        const expandParams: string[] = params.expand.split(",");
        for (const resources of data.data) {
            // type "as any" above because im not sure how to specify string as a key
            for (const name of expandParams) {
                if (resources[name]) {
                    populateResource(pluralize(name, 2), resources[name]);
                }
            }
        }
    }
};

export const populateResource = (name: string, resource: any) => {
    if (resource.id) {
        queryClient.setQueryData([name, resource.id], resource);
    }

    // Since the purchase is always returned for the `purchaseItems` calls,
    // we can check and populate the `purchases` queries
    if (name === "purchaseItems" && hasActualPurchaseData(resource)) {
        const purchaseWithExpandedItem = {
            ...resource.purchase,
            items: [resource],
        };

        queryClient.setQueryData(["purchases", resource.purchase.id], purchaseWithExpandedItem);

        // Populate queries with expanded waivers as well, we need it for the roster,
        // which will avoid us doing a lot of API calls (for each row in the table).
        if (hasActualWaiversData(resource)) {
            queryClient.setQueryData(
                ["purchases", resource.purchase.id, { expand: "waiver" }],
                purchaseWithExpandedItem,
            );
        }
    }

    if (name === "packages" && isPackageProduct(resource) && hasSelectedProductsExpanded(resource)) {
        const packageExperiences = getExpandedExperiencesInPackage(resource);
        for (const experience of packageExperiences) {
            queryClient.setQueryData(["experiences", experience.id], experience);
            queryClient.setQueryData(["products", experience.id], experience);
        }
    }
};

/**
 * This will populate data for useCollection and useInfiniteCollection
 * - We will do away with different cache key for useCollection later
 */
export const populateCollection = (name: string, data: any[]) => {
    queryClient.setQueryData([name, "collection"], data);
    // Also see `updateCollection` in pusher.js
    queryClient.setQueryData([name, "collection", "infinite"], { pages: [{ data }] });
};

export interface Page<T> {
    data: T[];
    next?: string | null;
}

export const flattenInfiniteData = <T>(data: InfiniteData<Page<T>> | undefined, defaultData: T[] = []) => {
    if (!data?.pages) {
        return defaultData;
    }

    return flatMap(data.pages, (page) => page.data) ?? defaultData;
};

const hasPurchaseExpanded = (resource: unknown): resource is ExperiencePurchaseItem => {
    return "purchase" in (resource as ExperiencePurchaseItem);
};

const hasActualPurchaseData = (resource: unknown): resource is ExperiencePurchaseItem => {
    if (hasPurchaseExpanded(resource)) {
        const { purchase } = resource;

        // Only return true if there is one `purchaseItem` since we can populate it data from resource,
        // in case when there are more we would need to fetch purchase,
        // to avoid case when we have multiple items and one has full data, and others just { id: ID }.
        return (
            "amount" in purchase &&
            "status" in purchase &&
            !isEmpty(purchase.lineItems) &&
            !isEmpty(purchase.items) &&
            purchase.items.length === 1
        );
    }

    return false;
};

interface PurchaseItemWithWaivers extends ExperiencePurchaseItem {
    waivers: Waiver[];
}

const hasActualWaiversData = (resource: unknown): resource is PurchaseItemWithWaivers => {
    if ("waivers" in (resource as PurchaseItemWithWaivers) && hasActualPurchaseData(resource)) {
        const { waivers } = resource;

        return !!waivers && Array.isArray(waivers) && (isEmpty(waivers) || "purchases" in waivers[0]);
    }

    return false;
};
